import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export const CTASection = () => {
  const [form, setForm] = useState({ name: '', business: '', phone: '', instagram: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
    } catch (err) {
      // Still show success to user even if backend fails
      console.error('Lead submission error:', err);
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/1a710595-688c-4b55-ac9a-4cf59bb327b0/images/fb3aae67c2d6fa14d5f406a6204316fdce96b8819a2025bae0a7d67d6d362d86.png"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-800/95" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Left: CTA copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5 flex flex-col justify-center"
          >
            <span className="font-accent text-slate-400 mb-6 block" data-testid="cta-label">
              Get Started
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white leading-tight mb-6"
              data-testid="cta-headline"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Ready to Get{' '}
              <span className="font-medium">Real Customers</span> from Instagram?
            </h2>
            <p className="text-base text-slate-400 leading-relaxed mb-8">
              Book a free strategy call. We'll audit your Instagram, show you what's
              missing, and give you a clear growth plan — no commitment required.
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <CheckCircle size={14} className="text-emerald-400" />
                Free audit
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle size={14} className="text-emerald-400" />
                No commitment
              </span>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-7 md:col-start-7"
          >
            {!submitted ? (
              <form
                onSubmit={handleSubmit}
                data-testid="lead-form"
                className="bg-white/10 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10"
              >
                <h3
                  className="text-xl font-medium text-white mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Book a Free Strategy Call
                </h3>

                <div className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm text-slate-300 mb-2 block">
                      Your Name
                    </Label>
                    <Input
                      id="name"
                      data-testid="form-name-input"
                      placeholder="John Doe"
                      value={form.name}
                      onChange={handleChange('name')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-white/30 h-12 rounded-xl"
                    />
                  </div>

                  <div>
                    <Label htmlFor="business" className="text-sm text-slate-300 mb-2 block">
                      Business Name
                    </Label>
                    <Input
                      id="business"
                      data-testid="form-business-input"
                      placeholder="Your Restaurant / Cafe / Business"
                      value={form.business}
                      onChange={handleChange('business')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-white/30 h-12 rounded-xl"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm text-slate-300 mb-2 block">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      data-testid="form-phone-input"
                      placeholder="+91 98765 43210"
                      value={form.phone}
                      onChange={handleChange('phone')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-white/30 h-12 rounded-xl"
                    />
                  </div>

                  <div>
                    <Label htmlFor="instagram" className="text-sm text-slate-300 mb-2 block">
                      Instagram Handle
                    </Label>
                    <Input
                      id="instagram"
                      data-testid="form-instagram-input"
                      placeholder="@yourbusiness"
                      value={form.instagram}
                      onChange={handleChange('instagram')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-white/30 h-12 rounded-xl"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="form-submit-button"
                    className="w-full bg-white text-slate-900 rounded-full px-8 py-4 text-sm font-semibold hover:bg-slate-100 transition-colors inline-flex items-center justify-center gap-2 group mt-2"
                  >
                    Get More Customers from Instagram
                    {submitting ? (
                      <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
                    ) : (
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white/10 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10 text-center"
                data-testid="form-success-message"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-emerald-400" />
                </div>
                <h3
                  className="text-2xl font-medium text-white mb-3"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  You're In!
                </h3>
                <p className="text-slate-400 text-sm">
                  Thanks, {form.name}! We'll reach out to discuss your Instagram growth strategy for {form.business}.
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
